import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { calculatePolygonArea } from '../utils/geometryUtils';
import {
  SnapPolygonMode,
  SnapPointMode,
  SnapLineMode,
  SnapModeDrawStyles,
  SnapDirectSelect,
} from 'mapbox-gl-draw-snap-mode';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

// Set your Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

interface Field {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number;
  geojson: string;
  state: string;
  county: string;
  cdl2024?: string; // Optional - only used when creating fields from map
  climate_zone: string;
  created_at: string;
  updated_at: string;
}

interface SetupMapProps {
  fields: Field[];
  selectedFieldId?: string; // For single field view
  mapStyle?: 'satellite' | 'street';
  showDrawingTools?: boolean; // Only for field editing
  onMapLoad?: (map: mapboxgl.Map) => void;
  onFieldUpdate?: (fieldId: string, updates: Partial<Field>) => void;
}

const SetupMap: React.FC<SetupMapProps> = ({
  fields,
  selectedFieldId,
  mapStyle = 'satellite',
  showDrawingTools = false,
  onMapLoad,
  onFieldUpdate
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  const popup = useRef<mapboxgl.Popup | null>(null);
  const isEditModeRef = useRef<boolean>(false); // Ref to track edit mode for event handlers
  const mousedownHandlerRef = useRef<((e: any) => void) | null>(null); // Ref to store mousedown handler
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVertex, setSelectedVertex] = useState<string | null>(null);
  const [selectedVertexCoords, setSelectedVertexCoords] = useState<[number, number] | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  // Create popup content for field hover
  const createPopupContent = (field: Field) => {
    const content = document.createElement('div');
    content.style.padding = '8px';
    content.style.fontSize = '12px';
    content.style.lineHeight = '1.4';
    
    // Show the requested information: business, farm, field, acres
    const lines = [
      `<strong>Field:</strong> ${field.fieldname}`,
      `<strong>Acres:</strong> ${field.acres.toFixed(1)}`
    ];
    
    content.innerHTML = lines.join('<br>');
    return content;
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/streets-v11',
      center: [-93.79028, 42.00849], // Default to Iowa
      zoom: 10
    });

    map.current.on('load', () => {
      setIsMapLoaded(true);
      
      // Initialize popup
      popup.current = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false
      });
      
      if (onMapLoad && map.current) {
        onMapLoad(map.current);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [mapStyle, onMapLoad]);

  // Load field polygons when map is ready and fields change
  useEffect(() => {
    if (!isMapLoaded || !map.current || !fields.length) return;

    // Remove existing field layers and sources
    if (map.current.getLayer('fields-fill')) {
      map.current.removeLayer('fields-fill');
    }
    if (map.current.getLayer('fields-stroke')) {
      map.current.removeLayer('fields-stroke');
    }
    if (map.current.getSource('fields')) {
      map.current.removeSource('fields');
    }

    // Create GeoJSON from fields - only include fields with geometry
    const fieldFeatures = fields
      .filter(field => field.geojson && field.geojson.trim() !== '') // Only include fields with geojson
      .map(field => {
        try {
          const geojson = JSON.parse(field.geojson);
          return {
            type: 'Feature' as const,
            geometry: geojson,
            properties: {
              field_id: field.field_id,
              fieldname: field.fieldname,
              acres: field.acres,
              isSelected: selectedFieldId === field.field_id
            }
          };
        } catch (error) {
          console.error('Error parsing field GeoJSON:', error);
          return null;
        }
      })
      .filter((feature): feature is NonNullable<typeof feature> => feature !== null);

    if (fieldFeatures.length === 0) return;

    const fieldCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: fieldFeatures
    };

    // Add field polygons to map - ensure style is loaded
    if (!map.current.isStyleLoaded()) {
      map.current.once('style.load', () => {
        if (map.current && !map.current.getSource('fields')) {
          map.current.addSource('fields', {
            type: 'geojson',
            data: fieldCollection
          });
          // Add layers after source is added
          if (!map.current.getLayer('fields-fill')) {
            map.current.addLayer({
              id: 'fields-fill',
              type: 'fill',
              source: 'fields',
              paint: {
                'fill-color': 'rgba(0, 255, 0, 0.05)',
                'fill-opacity': 0.05
              }
            });
          }
          if (!map.current.getLayer('fields-stroke')) {
            map.current.addLayer({
              id: 'fields-stroke',
              type: 'line',
              source: 'fields',
              paint: {
                'line-color': [
                  'case',
                  ['==', ['get', 'isSelected'], true],
                  '#FF0000',
                  '#00FF00'
                ],
                'line-width': [
                  'case',
                  ['==', ['get', 'isSelected'], true],
                  3,
                  2
                ]
              }
            });
          }
        }
      });
      return;
    }

    map.current.addSource('fields', {
      type: 'geojson',
      data: fieldCollection
    });

    // Add fill layer (transparent)
    map.current.addLayer({
      id: 'fields-fill',
      type: 'fill',
      source: 'fields',
      paint: {
        'fill-color': 'rgba(0, 255, 0, 0.05)', // Very light green, mostly transparent
        'fill-opacity': 0.05
      }
    });

    // Add stroke layer (bright light green)
    map.current.addLayer({
      id: 'fields-stroke',
      type: 'line',
      source: 'fields',
      paint: {
        'line-color': [
          'case',
          ['==', ['get', 'isSelected'], true],
          '#FF0000', // Red for selected field
          '#00FF00'  // Bright light green for others
        ],
        'line-width': [
          'case',
          ['==', ['get', 'isSelected'], true],
          3,  // Thicker red line for selected
          2   // Normal green line for others
        ]
      }
    });

    // Track current hovered field for smooth popup transitions
    let currentHoveredFieldId: string | null = null;

    // Add hover effect with popup
    map.current.on('mouseenter', 'fields-fill', (e) => {
      if (isEditModeRef.current) return; // Don't show popup in edit mode
      map.current!.getCanvas().style.cursor = 'pointer';
      
      if (e.features && e.features[0] && popup.current) {
        const feature = e.features[0];
        const fieldId = feature.properties?.field_id;
        const field = fields.find(f => f.field_id === fieldId);
        
        if (field) {
          // Always remove existing popup before showing new one
          if (popup.current.isOpen()) {
            popup.current.remove();
          }
          currentHoveredFieldId = fieldId;
          const popupContent = createPopupContent(field);
          popup.current.setLngLat(e.lngLat).setDOMContent(popupContent).addTo(map.current!);
        }
      }
    });

    map.current.on('mousemove', 'fields-fill', (e) => {
      if (isEditModeRef.current) return; // Don't show/update popup in edit mode
      
      if (e.features && e.features[0] && popup.current && popup.current.isOpen()) {
        const feature = e.features[0];
        const fieldId = feature.properties?.field_id;
        
        if (fieldId === currentHoveredFieldId) {
          // Same field - just update position
          popup.current.setLngLat(e.lngLat);
        } else {
          // Different field - update content and position
          currentHoveredFieldId = fieldId;
          const field = fields.find(f => f.field_id === fieldId);
          if (field) {
            if (popup.current.isOpen()) {
              popup.current.remove();
            }
            const popupContent = createPopupContent(field);
            popup.current.setLngLat(e.lngLat).setDOMContent(popupContent).addTo(map.current!);
          }
        }
      }
    });

    map.current.on('mouseleave', 'fields-fill', () => {
      map.current!.getCanvas().style.cursor = '';
      currentHoveredFieldId = null;
      if (popup.current && popup.current.isOpen()) {
        popup.current.remove();
      }
    });

    // Fit map to selected field bounds, or all fields if none selected
    let bounds = new mapboxgl.LngLatBounds();
    
    if (selectedFieldId) {
      // Zoom to just the selected field with extra padding
      const selectedFeature = fieldFeatures.find(f => f.properties?.field_id === selectedFieldId);
      if (selectedFeature && selectedFeature.geometry.type === 'Polygon') {
        selectedFeature.geometry.coordinates[0].forEach((coord: number[]) => {
          bounds.extend(coord as [number, number]);
        });
      }
    } else {
      // No selection, zoom to all fields
      fieldFeatures.forEach(feature => {
        if (feature?.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach((coord: number[]) => {
            bounds.extend(coord as [number, number]);
          });
        }
      });
    }

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 80,
        duration: 1000
      });
    }

  }, [isMapLoaded, fields, selectedFieldId]);

  // Initialize draw tool when entering edit mode
  useEffect(() => {
    if (!isMapLoaded || !isEditMode || !selectedFieldId || !map.current) {
      // Clean up draw control when exiting edit mode
      if (mousedownHandlerRef.current && map.current) {
        try { map.current.off('mousedown', mousedownHandlerRef.current); } catch (error) {}
        mousedownHandlerRef.current = null;
      }
      if (draw.current && map.current) {
        try {
          draw.current.deleteAll();
          map.current.removeControl(draw.current);
          // Also remove any remaining gl-draw-* layers that might persist
          if (map.current.isStyleLoaded()) {
            const allLayers = map.current.getStyle().layers || [];
            const glDrawLayers = allLayers.filter((layer: any) => 
              layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
            );
            glDrawLayers.reverse().forEach((layer: any) => {
              try {
                if (map.current && map.current.getLayer(layer.id)) {
                  map.current.removeLayer(layer.id);
                }
              } catch (e) {}
            });
          }
        } catch (error) {}
        draw.current = null;
      }
      return;
    }

    // Remove any existing gl-draw-* layers before creating new draw tool
    // This prevents "layer already exists" errors when re-entering edit mode
    if (map.current && map.current.isStyleLoaded()) {
      try {
        const allLayers = map.current.getStyle().layers || [];
        const glDrawLayers = allLayers.filter((layer: any) => 
          layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
        );
        // Remove layers in reverse order to avoid dependency issues
        glDrawLayers.reverse().forEach((layer: any) => {
          try {
            if (map.current && map.current.getLayer(layer.id)) {
              map.current.removeLayer(layer.id);
            }
          } catch (error) {
            // Layer might already be removed, ignore
          }
        });
      } catch (error) {
        // If style isn't ready yet, continue anyway
      }
    }

    // Initialize draw tool if not already created (reuse to avoid duplicate layers)
    if (!draw.current) {
      draw.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          point: false,
          line_string: false,
          polygon: false,
          trash: false
        },
        modes: {
          ...MapboxDraw.modes,
          // Use custom snap modes for drawing, but standard direct_select to avoid crashes
          draw_point: SnapPointMode as any,
          draw_polygon: SnapPolygonMode as any,
          draw_line_string: SnapLineMode as any,
          direct_select: SnapDirectSelect as any, // Use snap mode with robust coordinate handling
        } as any,
        userProperties: true,
        snap: true as any,
        snapOptions: {
          snapPx: 9,
          snapToMidPoints: true,
          snapVertexPriorityDistance: 0.0025,
          snapGetFeatures: (map: any, draw: any) => {
            // Get other field polygons to snap to
            const otherFields = fields.filter(f => f.field_id !== selectedFieldId);
            const otherFeatures = otherFields.map(field => {
              try {
                const geojson = JSON.parse(field.geojson);
                // Validate that the geometry has valid coordinates
                if (!geojson || !geojson.coordinates || !Array.isArray(geojson.coordinates)) {
                  return null;
                }
                // For polygons, ensure coordinates array is valid
                if (geojson.type === 'Polygon' && (!geojson.coordinates[0] || geojson.coordinates[0].length < 3)) {
                  return null;
                }
                return {
                  type: 'Feature' as const,
                  geometry: geojson,
                  properties: { field_id: field.field_id }
                };
              } catch (e) {
                return null;
              }
            }).filter(f => f !== null);
            
            // Also validate draw features before returning
            const drawFeatures = draw.getAll().features.filter((feature: any) => {
              return feature && 
                     feature.geometry && 
                     feature.geometry.coordinates && 
                     Array.isArray(feature.geometry.coordinates);
            });
            
            return [
              ...otherFeatures,
              ...drawFeatures
            ];
          }
        },
        guides: false,
        styles: (() => {
          // Custom styles - only include ones that override SnapModeDrawStyles or are new
          const customStyles = [
            {
              "id": "gl-draw-polygon-fill-active",
              "type": "fill",
              "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
              "paint": {
                "fill-color": "#FF0000",
                "fill-outline-color": "#FF0000",
                "fill-opacity": 0.15
              }
            },
            {
              "id": "gl-draw-polygon-stroke-active",
              "type": "line",
              "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
              "layout": {
                "line-cap": "round",
                "line-join": "round"
              },
              "paint": {
                "line-color": "#FF0000",
                "line-width": 3,
                "line-dasharray": ["literal", [1, 1.5]]
              }
            },
            {
              "id": "gl-draw-polygon-vertex-active",
              "type": "circle",
              "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
              "paint": {
                "circle-radius": 5,
                "circle-color": "#fff",
                "circle-stroke-color": "#FF0000",
                "circle-stroke-width": 2
              }
            },
            {
              "id": "gl-draw-polygon-vertex-inactive",
              "type": "circle",
              "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "mode", "static"]],
              "paint": {
                "circle-radius": 4,
                "circle-color": "#fff",
                "circle-stroke-color": "#FF0000",
                "circle-stroke-width": 1.5
              }
            },
            {
              "id": "gl-draw-polygon-vertex-selected",
              "type": "circle",
              "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "active", "true"]],
              "paint": {
                "circle-radius": 4,
                "circle-color": "#fff",
                "circle-stroke-color": "#FF0000",
                "circle-stroke-width": 3
              }
            }
          ];
          
          // Filter out SnapModeDrawStyles that we're overriding with custom styles
          const filteredSnapStyles = (SnapModeDrawStyles || []).filter((s: any) => {
            // Keep styles that aren't being overridden by our custom styles
            return !customStyles.some(cs => cs.id === s.id);
          });
          
          // Return: filtered snap styles first, then custom overrides (custom ones will override)
          // Note: MapboxDraw processes styles in order, so later definitions override earlier ones
          return [...filteredSnapStyles, ...customStyles];
        })()
      } as any);

      // Add draw tool to map
      // Ensure style is loaded and layers are cleaned before adding control
      // This follows Mapbox GL Draw best practices for proper cleanup/re-initialization
      const addDrawControlSafely = () => {
        if (!map.current || !draw.current) return;
        
        // Wait for style to be fully loaded
        if (!map.current.isStyleLoaded()) {
          map.current.once('style.load', () => {
            setTimeout(addDrawControlSafely, 100); // Small delay after style loads
          });
          return;
        }
        
        // Check if any gl-draw layers already exist (indicates control might be partially added)
        const hasExistingLayers = map.current.getStyle().layers.some((layer: any) => 
          layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
        );
        
        // If layers exist, remove them first (follows best practice: remove before re-add)
        if (hasExistingLayers) {
          try {
            const allLayers = map.current.getStyle().layers || [];
            const glDrawLayers = allLayers.filter((layer: any) => 
              layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
            );
            glDrawLayers.reverse().forEach((layer: any) => {
              try {
                if (map.current && map.current.getLayer(layer.id)) {
                  map.current.removeLayer(layer.id);
                }
              } catch (e) {}
            });
            // Wait a bit after removing layers before adding control
            setTimeout(() => addDrawControlSafely(), 50);
            return;
          } catch (e) {
            // If removal fails, continue anyway
          }
        }
        
        try {
          // Try to remove control first if it might already be on the map
          try {
            map.current.removeControl(draw.current);
          } catch (e) {
            // Control might not exist, which is fine
          }
          
          map.current.addControl(draw.current);
        } catch (error: any) {
          // Log unexpected errors (duplicate layer errors should not occur after filtering)
          console.warn('Error adding draw control:', error);
        }
      };
      
      addDrawControlSafely();

      // Add mouse event listener to track vertex selection
      const handleMouseDown = (e: any) => {
        if (!isEditModeRef.current || !draw.current) return;

        try {
          // Query for vertices using the correct layer name
          const features = map.current!.queryRenderedFeatures(e.point, {
            layers: ['gl-draw-polygon-vertex-active.cold']
          });

          if (features.length > 0) {
            const feature = features[0];
            // Get the actual feature ID from the draw tool
            const drawFeatures = draw.current.getAll();
            if (drawFeatures.features.length > 0) {
              const mainFeatureId = drawFeatures.features[0].id;

              if (feature.geometry.type === 'Point') {
                setSelectedVertexCoords(feature.geometry.coordinates as [number, number]);
              }
              setSelectedVertex(mainFeatureId as string);
            }
          } else {
            setSelectedVertex(null);
            setSelectedVertexCoords(null);
          }
        } catch (error) {
          setSelectedVertex(null);
        }
      };

      // Store handler in ref and add to map
      mousedownHandlerRef.current = handleMouseDown;
      map.current.on('mousedown', handleMouseDown);
    }

    // Find the selected field
    const selectedField = fields.find(field => field.field_id === selectedFieldId);
    if (!selectedField) return;

    // Load the field into the draw tool
    try {
      const fieldGeojson = JSON.parse(selectedField.geojson);

      // Clear any existing features - ensure draw tool is initialized
      if (!draw.current || typeof draw.current.deleteAll !== 'function') {
        console.error('Draw tool not properly initialized');
        return;
      }
      draw.current.deleteAll();

      // Add the field's polygon to the draw tool
      const features = draw.current.add({
        type: 'Feature',
        geometry: fieldGeojson,
        properties: {
          field_id: selectedFieldId
        }
      });

      // Enable direct select mode to show editable vertices
      if (features && features.length > 0) {
        const featureId = features[0];
        if (typeof featureId === 'string') {
          draw.current.changeMode('direct_select', { featureId });
        }
      }


    } catch (error) {
      console.error('❌ Error loading field into draw tool:', error);
    }

    // Cleanup function
    return () => {
      if (mousedownHandlerRef.current && map.current) {
        try {
          map.current.off('mousedown', mousedownHandlerRef.current);
        } catch (error) {}
        mousedownHandlerRef.current = null;
      }
      if (draw.current && map.current) {
        try {
          draw.current.deleteAll();
          map.current.removeControl(draw.current);
          // Also remove any remaining gl-draw-* layers that might persist
          if (map.current.isStyleLoaded()) {
            const allLayers = map.current.getStyle().layers || [];
            const glDrawLayers = allLayers.filter((layer: any) => 
              layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
            );
            glDrawLayers.reverse().forEach((layer: any) => {
              try {
                if (map.current && map.current.getLayer(layer.id)) {
                  map.current.removeLayer(layer.id);
                }
              } catch (e) {}
            });
          }
        } catch (error) {}
        draw.current = null;
      }
    };
  }, [isMapLoaded, isEditMode, selectedFieldId, fields]);

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (!selectedFieldId) return;

    if (isEditMode) {
      // Exit edit mode
      if (draw.current && typeof draw.current.deleteAll === 'function') {
        draw.current.deleteAll();
      }
      setIsEditMode(false);
      setSelectedVertex(null);
      setSelectedVertexCoords(null);
    } else {
      // Enter edit mode - close popup if open
      if (popup.current && popup.current.isOpen()) {
        popup.current.remove();
      }
      setIsEditMode(true);
    }
  };

  // Handle removing selected vertex
  const handleRemoveVertex = () => {
    if (!selectedVertex || !draw.current || !selectedVertexCoords) return;



    try {
      // Get the current feature
      const data = draw.current.getAll();
      if (data.features.length > 0) {
        const feature = data.features[0];


        if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
          // Polygons have an array of linear rings; the first is the outer ring
          let coordinates = feature.geometry.coordinates[0];



          // Find the vertex index by comparing coordinates
          let vertexIndex = -1;
          let closestIndex = -1;
          let closestDistance = Infinity;

          for (let i = 0; i < coordinates.length; i++) {
            const coord = coordinates[i];


            // Calculate distance between coordinates
            const distance = Math.sqrt(
              Math.pow(coord[0] - selectedVertexCoords[0], 2) +
              Math.pow(coord[1] - selectedVertexCoords[1], 2)
            );

            // Track closest vertex
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = i;
            }

            // Compare coordinates with very precise tolerance (0.000001 degrees ≈ 11 cm)
            if (Math.abs(coord[0] - selectedVertexCoords[0]) < 0.000001 &&
                Math.abs(coord[1] - selectedVertexCoords[1]) < 0.000001) {
              vertexIndex = i;

              break;
            }
          }

          // If no exact match, use the closest vertex
          if (vertexIndex === -1 && closestIndex !== -1) {

            vertexIndex = closestIndex;
          }



          if (vertexIndex >= 0 && vertexIndex < coordinates.length) {
            coordinates.splice(vertexIndex, 1); // Remove the vertex

            // Update the feature in the draw tool
            draw.current.set({
              type: 'FeatureCollection',
              features: [feature]
            });


          } else {

          }
        }
        // Clear the selection
        setSelectedVertex(null);
        setSelectedVertexCoords(null);
      }
    } catch (error) {
      console.error('❌ Error in remove vertex:', error);
    }
  };

  // Handle saving field changes
  const handleSaveChanges = () => {
    if (!selectedFieldId || !draw.current || !onFieldUpdate) return;



    try {
      // Get the updated GeoJSON from the draw tool
      const data = draw.current.getAll();

      if (data.features.length > 0) {
        const updatedGeometry = data.features[0].geometry;
        const updatedGeojson = JSON.stringify(updatedGeometry);
        
        // Calculate the updated acres from the new geometry
        const updatedAcres = calculatePolygonArea(updatedGeometry);

        // Call the update callback with both geojson and acres
        onFieldUpdate(selectedFieldId, { 
          geojson: updatedGeojson,
          acres: updatedAcres
        });



        // Refresh the draw tool display to show the updated polygon
        if (draw.current && typeof draw.current.deleteAll === 'function' && typeof draw.current.add === 'function') {
          // Force a refresh by re-adding the updated feature
          const currentFeature = data.features[0];
          draw.current.deleteAll();
          draw.current.add(currentFeature);
        }

        // Exit edit mode after saving
        setIsEditMode(false);
        setSelectedVertex(null);
        setSelectedVertexCoords(null);
      }
    } catch (error) {
      console.error('❌ Error saving field changes:', error);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '700px' }}>
      {/* Edit Field Button - only show if we have a selected field */}
      {selectedFieldId && (
        <button
          onClick={handleEditToggle}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 100,
            backgroundColor: isEditMode ? '#dc3545' : '#D1FF6E',
            color: isEditMode ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          title="Edit Field Boundary"
        >
          {isEditMode ? 'Exit Edit' : 'Edit Field Boundary'}
        </button>
      )}

      {/* Save Changes Button - only show in edit mode */}
      {isEditMode && onFieldUpdate && (
        <button
          onClick={handleSaveChanges}
          style={{
            position: 'absolute',
            top: '50px',
            left: '10px',
            zIndex: 100,
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          title="Save Field Changes"
        >
          Save Changes
        </button>
      )}

      {/* Remove Selected Point Button - only show in edit mode when vertex is selected */}
      {isEditMode && selectedVertex && (
        <button
          onClick={handleRemoveVertex}
          style={{
            position: 'absolute',
            top: '90px',
            left: '10px',
            zIndex: 100,
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
          title="Remove Selected Point"
        >
          Remove Selected Point
        </button>
      )}

      <div
        ref={mapContainer}
        style={{
          width: '100%',
          height: 'calc(105vh - 445px)',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: isEditMode ? 'pointer' : 'default'
        }}
      />
    </div>
  );
};

export default SetupMap;
